const BEST_KEY = 'abcs.bestRoundMs.v1';
const BEST_SURVIVAL_KEY = 'abcs.bestSurvival.v1';
const BEST_PAIRS_KEY = 'abcs.bestPairs.v1';
const BEST_MAZE_KEY = 'abcs.bestMaze.v1';
const BEST_LABYRINTH_KEY = 'abcs.bestLabyrinth.v1';
const BEST_RECIPE_KEY = 'abcs.bestRecipe.v1';
const BEST_ROCKET_KEY = 'abcs.bestRocketRound.v1';
const BEST_WAGON_KEY = 'abcs.bestWagon.v1';
const BG_KEY = 'abcs.background.v1';
const VOICE_MUTE_KEY = 'abcs.voiceMute.v1';
const VOICE_HINT_KEY = 'abcs.voiceHintShown.v1';    // { [lang]: true } — hint already shown
const MUSIC_MUTE_KEY = 'abcs.musicMute.v1';
const SCORE_SEEN_KEY = 'abcs.scoreboardSeen.v1';    // { [packKey]: signature } — card already read
const LESSON_KEY = 'abcs.selectedLesson.v2';        // { [lang]: packKey }
const LESSON_KEY_V1 = 'abcs.selectedLesson.v1';     // pre-v2: one packKey shared by all languages

/**
 * Every localStorage touch in this file goes through these three.
 *
 * A browser that blocks site data does not hand back an empty store — the property access itself
 * throws `SecurityError` (Chrome/Edge "Block all cookies", Firefox "Block cookies: All", locked-down
 * kiosk WebViews), and `setItem` throws `QuotaExceededError` on a full origin. That matters here
 * more than anywhere else in the app because these reads sit on the first interactive frame:
 * `getBackground` runs in MenuScene.create, `getMusicMuted`/`getVoiceMuted` on every board. An
 * uncaught throw there is a blank canvas with no way back.
 *
 * A blocked store means the player keeps no records and no settings — every getter falls back to
 * the same default a first-time player gets. That is the right outcome; a crash is not. This is the
 * guard progression/store.ts, clip.ts, rating.ts and platform/index.ts already apply to their own
 * writes, applied here too.
 */
function readRaw(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage blocked or full — the setting simply does not persist */
  }
}

function removeRaw(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* see writeRaw */
  }
}

/**
 * Last lesson the player picked, remembered PER UI LANGUAGE (one packKey per language).
 * A lesson is language-scoped, so a single global pick meant a de → en → de round-trip dropped
 * the player back on the German default; with a per-language map each language keeps its own
 * lesson across switches. See getActivePack in src/game/packs.ts.
 */
type LessonMap = Record<string, string>;

function readLessonMap(): LessonMap {
  let map: LessonMap = {};
  try {
    const raw = readRaw(LESSON_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      for (const [lang, key] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof key === 'string') map[lang] = key;
      }
    }
  } catch {
    map = {};   // corrupt entry: start over rather than trap the player on a broken pick
  }
  // Forward-migrate the pre-v2 single pick. A packKey is `<packId>__<lang>`, so it carries the
  // language it belonged to — file it under that language and drop the old entry.
  const legacy = readRaw(LESSON_KEY_V1);
  if (legacy !== null) {
    const sep = legacy.lastIndexOf('__');
    const lang = sep > 0 ? legacy.slice(sep + 2) : '';
    if (lang && map[lang] === undefined) map[lang] = legacy;
    removeRaw(LESSON_KEY_V1);
    writeLessonMap(map);
  }
  return map;
}

function writeLessonMap(map: LessonMap): void {
  writeRaw(LESSON_KEY, JSON.stringify(map));
}

/** The lesson this language was last played on (a packKey). Null if never picked in it. */
export function getSelectedLessonKey(lang: string): string | null {
  return readLessonMap()[lang] ?? null;
}

export function setSelectedLessonKey(lang: string, key: string): void {
  const map = readLessonMap();
  map[lang] = key;
  writeLessonMap(map);
}

export function getBestRoundMs(): number | null {
  const raw = readRaw(BEST_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function setBestRoundMs(ms: number): void {
  writeRaw(BEST_KEY, String(Math.round(ms)));
}

/** Best Survival-mode score (most words cleared in one run). Global, not per-language. */
export function getBestSurvival(): number {
  const raw = readRaw(BEST_SURVIVAL_KEY);
  const n = raw ? Number(raw) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function setBestSurvival(score: number): void {
  writeRaw(BEST_SURVIVAL_KEY, String(Math.max(0, Math.round(score))));
}

/** Best Pairs score (most pairs found in one run). Global, not per-language. */
export function getBestPairs(): number {
  const raw = readRaw(BEST_PAIRS_KEY);
  const n = raw ? Number(raw) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function setBestPairs(pairs: number): void {
  writeRaw(BEST_PAIRS_KEY, String(Math.max(0, Math.round(pairs))));
}

/** Best Ice Maze score (most words found in one run). Global, not per-language. */
export function getBestMaze(): number {
  const raw = readRaw(BEST_MAZE_KEY);
  const n = raw ? Number(raw) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function setBestMaze(words: number): void {
  writeRaw(BEST_MAZE_KEY, String(Math.max(0, Math.round(words))));
}

/**
 * Rounds cleared in the best Word Labyrinth run (specs/labyrinth-mode.md §8.3).
 *
 * Per device rather than per lesson, like the Pairs and Maze records: the labyrinth is the same
 * game whichever journey paid for it, so one number is the honest one.
 */
export function getBestLabyrinth(): number {
  const raw = Number(readRaw(BEST_LABYRINTH_KEY));
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
}

export function setBestLabyrinth(rounds: number): void {
  writeRaw(BEST_LABYRINTH_KEY, String(Math.max(0, Math.floor(rounds))));
}

/**
 * Dishes served in the best Recipe Kitchen run (specs/recipe-mode.md §4.4).
 *
 * Per device rather than per lesson, like the Pairs, Maze and Labyrinth records: the kitchen is the
 * same kitchen whichever journey paid for it, so one number is the honest one.
 */
export function getBestRecipe(): number {
  const raw = Number(readRaw(BEST_RECIPE_KEY));
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
}

/**
 * The furthest round reached in a Rocket Read run (specs/rocket-read-mode.md §4.2). Per device, like
 * the Recipe record: the content is the language's comprehension pack whichever lesson paid for it.
 */
export function getBestRocket(): number {
  const raw = Number(readRaw(BEST_ROCKET_KEY));
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
}

export function setBestRocket(round: number): void {
  writeRaw(BEST_ROCKET_KEY, String(Math.max(0, Math.floor(round))));
}

export function setBestRecipe(dishes: number): void {
  writeRaw(BEST_RECIPE_KEY, String(Math.max(0, Math.floor(dishes))));
}

/** Best Word Wagon score (most loads shipped in one run). Global, not per-language. */
export function getBestWagon(): number {
  const raw = readRaw(BEST_WAGON_KEY);
  const n = raw ? Number(raw) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function setBestWagon(loads: number): void {
  writeRaw(BEST_WAGON_KEY, String(Math.max(0, Math.round(loads))));
}

export type Background = 'space' | 'nature' | 'school' | 'voxelworld' | 'fable' | 'pagoda';
export const BACKGROUNDS: Background[] = ['space', 'nature', 'school', 'voxelworld', 'fable', 'pagoda'];

export function getBackground(): Background {
  const v = readRaw(BG_KEY) as Background | null;
  return v && (BACKGROUNDS as string[]).includes(v) ? v : 'space';
}

export function setBackground(bg: Background): void {
  writeRaw(BG_KEY, bg);
}

/**
 * Languages the player has already been shown the "no voice installed for this language" hint for.
 *
 * Per language, not global: a device can have a German voice but no Thai one, so a player who
 * switches de → th needs telling again. Stored as a set of language tags ({ th: true }).
 * See src/ui/voiceHintOverlay.ts.
 */
function readVoiceHintShown(): Record<string, true> {
  try {
    const raw = readRaw(VOICE_HINT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, true> = {};
    for (const [lang, seen] of Object.entries(parsed as Record<string, unknown>)) {
      if (seen === true) out[lang] = true;
    }
    return out;
  } catch {
    return {};   // corrupt entry: at worst the hint shows once more, which is harmless
  }
}

export function getVoiceHintShown(lang: string): boolean {
  return readVoiceHintShown()[lang] === true;
}

export function setVoiceHintShown(lang: string): void {
  const map = readVoiceHintShown();
  map[lang] = true;
  writeRaw(VOICE_HINT_KEY, JSON.stringify(map));
}

/**
 * Per lesson, the record card's contents AS OF the last time the card was opened — a short
 * signature, not the numbers themselves (see scoreboardSignature). The menu pulses the reward
 * target while the stored signature differs from the lesson's current one, which is exactly "the
 * records changed and nobody has looked".
 *
 * UI state, not a record, so it lives here rather than in the lesson's mastery blob: a progress
 * reset wipes the records themselves, and a stale signature left behind cannot mislead — an empty
 * scoreboard has nothing to pulse about (see hasUnseenRecords).
 */
function readScoreboardSeen(): Record<string, string> {
  try {
    const raw = readRaw(SCORE_SEEN_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, string> = {};
    for (const [key, sig] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof sig === 'string') out[key] = sig;
    }
    return out;
  } catch {
    return {};   // corrupt entry: at worst the target pulses once more, which is harmless
  }
}

export function getScoreboardSeen(packKey: string): string | null {
  return readScoreboardSeen()[packKey] ?? null;
}

export function setScoreboardSeen(packKey: string, signature: string): void {
  const map = readScoreboardSeen();
  map[packKey] = signature;
  writeRaw(SCORE_SEEN_KEY, JSON.stringify(map));
}

export function getVoiceMuted(): boolean {
  return readRaw(VOICE_MUTE_KEY) === '1';
}

export function setVoiceMuted(muted: boolean): void {
  writeRaw(VOICE_MUTE_KEY, muted ? '1' : '0');
}

/**
 * Background ambience mute. When set, no backdrop ambience bed is started.
 * Defaults to muted for first-time players (no stored choice); once the player toggles it,
 * their explicit choice is saved and respected.
 */
export function getMusicMuted(): boolean {
  const raw = readRaw(MUSIC_MUTE_KEY);
  return raw === null ? true : raw === '1';
}

export function setMusicMuted(muted: boolean): void {
  writeRaw(MUSIC_MUTE_KEY, muted ? '1' : '0');
}
